import { computed, type Ref } from 'vue';

type FlowDialogErrorsOptions = {
  errorsRaw: Ref<unknown[][] | null | undefined>;
  errorCondition: Ref<unknown[]>;
};

const getErrors = (
  errorsRaw: unknown[][] | null | undefined,
  errorCondition: unknown[],
  outIndexes?: number[],
) =>
  errorsRaw
    ? errorsRaw.filter((error, index) => {
        const result = errorCondition.every((item, conditionIndex) => item === error[conditionIndex]);
        if (outIndexes && result) {
          outIndexes.push(index);
        }
        return result;
      })
    : [];

const getInvalidFieldsError = (
  errorsRaw: unknown[][] | null | undefined,
  errorCondition: unknown[],
  condition: unknown[],
  reverse = false,
) => {
  const errors = getErrors(errorsRaw, errorCondition);
  const index = errorCondition.length;
  const conditionFunc = (error: unknown[]) =>
    reverse ? !condition.includes(error[index]) : condition.includes(error[index]);
  return errors.some(conditionFunc);
};

const getEmptyEntityError = (
  errorsRaw: unknown[][] | null | undefined,
  errorCondition: unknown[],
  entityCondition: unknown[],
) => {
  const errors = getErrors(errorsRaw, errorCondition);
  const index = errorCondition.length;
  return errors.some((error) => {
    for (let conditionIndex = index, entityIndex = 0; entityIndex < entityCondition.length; conditionIndex++, entityIndex++) {
      if (error[conditionIndex] !== entityCondition[entityIndex]) {
        return false;
      }
    }
    return true;
  });
};

const getFieldErrorMessages = (isError: boolean) => (isError ? 'error' : null);

export const useFlowDialogFieldErrors = ({
  errorsRaw,
  errorCondition,
}: FlowDialogErrorsOptions) => {
  const isNoConditionError = computed(() =>
    getInvalidFieldsError(errorsRaw.value, errorCondition.value, [
      'VALIDATION_FLOW_RULE_AT_LEAST_ONE_CONDITION',
      'conditions',
    ]),
  );

  const isAtLeastOneActivePathError = computed(() =>
    getInvalidFieldsError(errorsRaw.value, errorCondition.value, [
      'VALIDATION_FLOW_AT_LEAST_ONE_ACTIVE_PATH',
      'paths',
      0,
    ]),
  );

  const isAnyRuleHasError = computed(() =>
    getInvalidFieldsError(errorsRaw.value, errorCondition.value, [
      'VALIDATION_FLOW_CONDITION_EMPTY_PARAMETER',
    ]),
  );

  return {
    isNoConditionError,
    isAtLeastOneActivePathError,
    isAnyRuleHasError,
  };
};

export const useFlowPathFieldErrors = ({
  errorsRaw,
  errorCondition,
}: FlowDialogErrorsOptions) => {
  const emptyLandingsCondition = ['landings', 1, 'VALIDATION_FLOW_PATH_AT_LEAST_ONE_LANDING'];
  const emptyOffersCondition = ['offers', 1, 'VALIDATION_FLOW_PATH_AT_LEAST_ONE_OFFER'];
  const pathNameErrorCondition = ['name'];
  const pathWeightErrorCondition = ['weight'];

  const isEmptyLandingsError = computed(() =>
    getEmptyEntityError(errorsRaw.value, errorCondition.value, emptyLandingsCondition),
  );

  const isEmptyOffersError = computed(() =>
    getEmptyEntityError(errorsRaw.value, errorCondition.value, emptyOffersCondition),
  );

  const isNameError = computed(() =>
    getInvalidFieldsError(errorsRaw.value, errorCondition.value, pathNameErrorCondition),
  );

  const isWeightError = computed(() =>
    getInvalidFieldsError(errorsRaw.value, errorCondition.value, pathWeightErrorCondition),
  );

  const nameErrorMessages = computed(() => getFieldErrorMessages(isNameError.value));
  const weightErrorMessages = computed(() => getFieldErrorMessages(isWeightError.value));

  return {
    isEmptyLandingsError,
    isEmptyOffersError,
    isNameError,
    isWeightError,
    nameErrorMessages,
    weightErrorMessages,
  };
};

export const useFlowItemFieldErrors = ({
  errorsRaw,
  errorCondition,
}: FlowDialogErrorsOptions) => {
  const generalErrorCondition = ['id'];
  const itemWeightErrorCondition = ['weight'];

  const isGeneralError = computed(() =>
    getInvalidFieldsError(errorsRaw.value, errorCondition.value, generalErrorCondition),
  );

  const generalErrorMessages = computed(() => getFieldErrorMessages(isGeneralError.value));

  const isWeightError = computed(() =>
    getInvalidFieldsError(errorsRaw.value, errorCondition.value, itemWeightErrorCondition),
  );

  const weightErrorMessages = computed(() => getFieldErrorMessages(isWeightError.value));

  return {
    isGeneralError,
    generalErrorMessages,
    isWeightError,
    weightErrorMessages,
  };
};
